import React, { useEffect, useState, useCallback } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useLanguage } from "../context/LanguageContext";
import { useHistory } from "../context/HistoryContext";
import { useFavorites } from "../context/FavoritesContext";
import { useReminders } from "../context/ReminderContext";
import { calculateProfit, calculateDaysToTarget, calculateRiskScenarios, CALC_TYPES, PERIODS } from "../utils/calculations";
import { exportToPDF, exportToExcel } from "../utils/exportUtils";
import { useExchangeRate } from "../context/ExchangeRateContext";
import useAnimatedNumber from "../hooks/useAnimatedNumber";
import {
    LuCheck,
    LuX,
    LuInfo,
    LuTriangleAlert,
    LuStar,
    LuShare2,
    LuLayoutGrid,
    LuBell,
    LuTrash2,
    LuCopy,
    LuMessageCircle,
    LuSend,
    LuRefreshCw,
    LuTrendingUp
} from "react-icons/lu";
import { FaXTwitter } from "react-icons/fa6";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Animated Value Component
function AnimatedValue({ value, formatter }) {
    const animatedValue = useAnimatedNumber(value, 800);
    return <span className="animated-value">{formatter(animatedValue)}</span>;
}

// Toast Component
function Toast({ toast, onClose }) {
    if (!toast) return null;

    const icons = {
        success: <LuCheck />,
        error: <LuX />,
        info: <LuInfo />,
        warning: <LuTriangleAlert />
    };

    return (
        <div className={`toast ${toast.type}`}>
            <span className="toast-icon">{icons[toast.type]}</span>
            <div className="toast-content">
                <div className="toast-title">{toast.title}</div>
                <div className="toast-message">{toast.message}</div>
            </div>
            <button className="toast-close" onClick={onClose}><LuX /></button>
        </div>
    );
}

// Empty State Component
function EmptyState({ icon, title, description }) {
    return (
        <div className="empty-state-enhanced">
            <div className="empty-state-illustration">
                <svg viewBox="0 0 24 24">
                    {icon === 'chart' && (
                        <path d="M3 3v18h18M7 16l4-4 4 4 5-6" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                    {icon === 'history' && (
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                    {icon === 'star' && (
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                </svg>
            </div>
            <h3 className="empty-state-title">{title}</h3>
            <p className="empty-state-description">{description}</p>
        </div>
    );
}

// Share Modal Component
function ShareModal({ isOpen, onClose, shareUrl, t }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareLinks = {
        twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Getiri hesaplamam!')}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">{t('share.title')}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="share-url-container">
                    <input
                        type="text"
                        className="share-url-input"
                        value={shareUrl}
                        readOnly
                    />
                    <button
                        className={`btn-copy ${copied ? 'copied' : ''}`}
                        onClick={handleCopy}
                    >
                        {copied ? <LuCheck /> : <><LuCopy /> {t('share.copyLink')}</>}
                    </button>
                </div>
                <p className="text-center mb-2" style={{ color: 'var(--text-secondary)' }}>{t('share.shareVia')}</p>
                <div className="share-options">
                    <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="share-btn"><FaXTwitter /></a>
                    <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="share-btn"><LuMessageCircle /></a>
                    <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" className="share-btn"><LuSend /></a>
                </div>
            </div>
        </div>
    );
}

// Reminder Modal Component
function ReminderModal({ isOpen, onClose, onAdd, currentAmount, t }) {
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [message, setMessage] = useState('');
    const [targetAmount, setTargetAmount] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onAdd({
            title,
            date: new Date(date).toISOString(),
            message,
            targetAmount: Number(targetAmount) || currentAmount
        });
        setTitle('');
        setDate('');
        setMessage('');
        setTargetAmount('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">{t('reminder.add')}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-group mb-2">
                        <label className="form-label">{t('reminder.name')}</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder={t('reminder.namePlaceholder')}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label className="form-label">{t('reminder.date')}</label>
                        <input
                            type="datetime-local"
                            className="form-input"
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label className="form-label">{t('reminder.targetAmount')}</label>
                        <input
                            type="number"
                            className="form-input"
                            placeholder="orn: 10000"
                            value={targetAmount}
                            onChange={e => setTargetAmount(e.target.value)}
                        />
                    </div>
                    <div className="form-group mb-3">
                        <label className="form-label">{t('reminder.message')}</label>
                        <textarea
                            className="form-input"
                            placeholder={t('reminder.messagePlaceholder')}
                            rows="3"
                            style={{ resize: 'vertical' }}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                        {t('reminder.add')}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Home() {
    const { t, lang } = useLanguage();
    const { addToHistory, history } = useHistory();
    const { favorites, addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
    const { reminders, addReminder, removeReminder, notification, dismissNotification, requestNotificationPermission } = useReminders();
    const { rates, loading: ratesLoading, error: ratesError, lastUpdated, refreshRates } = useExchangeRate();

    // Form state
    const [balance, setBalance] = useState("");
    const [targetDay, setTargetDay] = useState("");
    const [percentage, setPercentage] = useState("");
    const [investmentRatio, setInvestmentRatio] = useState("");
    const [calcType, setCalcType] = useState(CALC_TYPES.COMPOUND);
    const [period, setPeriod] = useState(PERIODS.DAILY);
    const [inflation, setInflation] = useState("");

    // Validation state
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Results state
    const [data, setData] = useState([]);
    const [currency, setCurrency] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Target calculator state
    const [targetAmount, setTargetAmount] = useState("");
    const [daysToTarget, setDaysToTarget] = useState(null);

    // Risk analysis state
    const [riskScenarios, setRiskScenarios] = useState([]);

    // UI state
    const [activeTab, setActiveTab] = useState("calculator");
    const [showHistory, setShowHistory] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [toast, setToast] = useState(null);

    // Comparison state
    const [comparisons, setComparisons] = useState([]);

    const currencyFormatterUsdt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });

    const currencyFormatter = new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2
    });

    // Show toast
    const showToast = useCallback((type, title, message) => {
        setToast({ type, title, message });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // Validation
    const validateField = useCallback((name, value) => {
        if (!value || value === '') {
            return t('form.validation.required');
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
            return t('form.validation.invalidNumber');
        }
        if (numValue <= 0) {
            return t('form.validation.minValue');
        }
        if ((name === 'percentage' || name === 'investmentRatio') && numValue > 100) {
            return t('form.validation.maxPercent');
        }
        return null;
    }, [t]);

    const handleBlur = (name, value) => {
        setTouched(prev => ({ ...prev, [name]: true }));
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    // Generate share URL
    const generateShareUrl = useCallback(() => {
        const params = new URLSearchParams({
            b: balance,
            d: targetDay,
            p: percentage,
            r: investmentRatio,
            t: calcType,
            pr: period,
            i: inflation || 0
        });
        return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    }, [balance, targetDay, percentage, investmentRatio, calcType, period, inflation]);

    // Load from URL params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('b')) {
            setBalance(params.get('b'));
            setTargetDay(params.get('d') || '');
            setPercentage(params.get('p') || '');
            setInvestmentRatio(params.get('r') || '');
            setCalcType(params.get('t') || CALC_TYPES.COMPOUND);
            setPeriod(params.get('pr') || PERIODS.DAILY);
            setInflation(params.get('i') || '');
        }
    }, []);

    // Fetch exchange rate
    useEffect(() => {
        const fetchExchangeRate = async () => {
            try {
                const response = await fetch('https://open.er-api.com/v6/latest/USD');
                const result = await response.json();
                setCurrency(result.rates.TRY);
            } catch (error) {
                console.error('Doviz kuru alinamadi:', error.message);
                setCurrency(35);
            }
        };
        fetchExchangeRate();
    }, []);

    // Request notification permission on mount
    useEffect(() => {
        requestNotificationPermission();
    }, [requestNotificationPermission]);

    const handleCalculate = async () => {
        // Validate all fields
        const fieldsToValidate = { balance, targetDay, percentage, investmentRatio };
        const newErrors = {};
        let hasErrors = false;

        Object.entries(fieldsToValidate).forEach(([name, value]) => {
            const error = validateField(name, value);
            if (error) {
                newErrors[name] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);
        setTouched({ balance: true, targetDay: true, percentage: true, investmentRatio: true });

        if (hasErrors) return;

        setIsLoading(true);

        // Simulate loading for better UX
        await new Promise(resolve => setTimeout(resolve, 500));

        const results = calculateProfit({
            balance,
            investmentRatio,
            percentage,
            targetDay,
            calcType,
            period,
            inflation: inflation || 0,
            currency
        });

        setData(results);

        // Calculate risk scenarios
        const risks = calculateRiskScenarios({
            balance,
            investmentRatio,
            percentage,
            targetDay,
            currency
        });
        setRiskScenarios(risks);

        // Add to history
        addToHistory({
            balance: Number(balance),
            investmentRatio: Number(investmentRatio),
            percentage: Number(percentage),
            targetDay: Number(targetDay),
            calcType,
            period,
            inflation: Number(inflation) || 0,
            finalBalance: results.length > 0 ? results[results.length - 1].usdt : 0,
            totalProfit: results.length > 0 ? results[results.length - 1].usdt - Number(balance) : 0
        });

        setIsLoading(false);
        showToast('success', t('toast.success'), t('loading.calculating'));
    };

    const handleReset = () => {
        setBalance("");
        setTargetDay("");
        setPercentage("");
        setInvestmentRatio("");
        setInflation("");
        setData([]);
        setRiskScenarios([]);
        setDaysToTarget(null);
        setTargetAmount("");
        setErrors({});
        setTouched({});
    };

    const handleTargetCalculate = () => {
        if (!balance || !percentage || !investmentRatio || !targetAmount) return;

        const days = calculateDaysToTarget({
            balance,
            investmentRatio,
            percentage,
            targetAmount,
            calcType
        });

        setDaysToTarget(days);
    };

    const loadFromHistory = (item) => {
        setBalance(item.balance.toString());
        setInvestmentRatio(item.investmentRatio.toString());
        setPercentage(item.percentage.toString());
        setTargetDay(item.targetDay.toString());
        setCalcType(item.calcType);
        setPeriod(item.period);
        setInflation(item.inflation.toString());
        setShowHistory(false);
        setShowFavorites(false);
    };

    const handleToggleFavorite = () => {
        const params = {
            balance: Number(balance),
            percentage: Number(percentage),
            targetDay: Number(targetDay),
            investmentRatio: Number(investmentRatio),
            calcType,
            period,
            inflation: Number(inflation) || 0
        };

        if (isFavorite(params)) {
            const fav = favorites.find(f =>
                f.balance === params.balance &&
                f.percentage === params.percentage
            );
            if (fav) {
                removeFromFavorites(fav.id);
                showToast('info', t('toast.info'), t('favorites.removed'));
            }
        } else {
            addToFavorites(params);
            showToast('success', t('toast.success'), t('favorites.added'));
        }
    };

    const handleAddComparison = () => {
        if (data.length === 0) return;

        const newComparison = {
            id: Date.now(),
            name: `${t('comparison.scenario')} ${comparisons.length + 1}`,
            params: { balance, percentage, targetDay, investmentRatio, calcType, period, inflation },
            finalBalance: getFinalBalance(),
            totalProfit: getTotalProfit()
        };

        setComparisons(prev => [...prev, newComparison].slice(0, 3));
        showToast('success', t('toast.success'), t('comparison.add'));
    };

    const handleRemoveComparison = (id) => {
        setComparisons(prev => prev.filter(c => c.id !== id));
    };

    const handleAddReminder = (reminderData) => {
        addReminder(reminderData);
        showToast('success', t('toast.success'), t('reminder.created'));
    };

    const getTotalProfit = () => {
        if (data.length === 0) return 0;
        return data[data.length - 1].usdt - Number(balance);
    };

    const getFinalBalance = () => {
        if (data.length === 0) return Number(balance) || 0;
        return data[data.length - 1].usdt;
    };

    const getAverageDaily = () => {
        if (data.length === 0) return 0;
        return getTotalProfit() / data.length;
    };

    const getRealProfit = () => {
        if (data.length === 0 || !inflation) return getTotalProfit();
        return data[data.length - 1].realValue - Number(balance);
    };

    const chartData = {
        labels: data.map(item => `${item.day}. ${t('results.day')}`),
        datasets: [
            {
                label: t('chart.balance'),
                data: data.map(item => item.usdt),
                borderColor: '#00d4ff',
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#00d4ff',
                pointBorderColor: '#00d4ff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: '#00d4ff',
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(18, 18, 26, 0.9)',
                titleColor: '#fff',
                bodyColor: '#9ca3af',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                padding: 12,
                displayColors: false,
                callbacks: {
                    label: (context) => currencyFormatterUsdt.format(context.parsed.y)
                }
            }
        },
        scales: {
            x: {
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: { color: '#6b7280' }
            },
            y: {
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: {
                    color: '#6b7280',
                    callback: (value) => '$' + value.toLocaleString()
                }
            }
        }
    };

    const handleExportPDF = () => {
        const stats = {
            initialBalance: Number(balance),
            finalBalance: getFinalBalance(),
            totalProfit: getTotalProfit(),
            dailyAverage: getAverageDaily(),
            profitRate: ((getFinalBalance() - Number(balance)) / Number(balance) * 100)
        };
        exportToPDF(data, stats, lang);
        showToast('success', t('export.success'), t('export.pdfDownloaded'));
    };

    const handleExportExcel = () => {
        const stats = {
            initialBalance: Number(balance),
            finalBalance: getFinalBalance(),
            totalProfit: getTotalProfit(),
            dailyAverage: getAverageDaily(),
            profitRate: ((getFinalBalance() - Number(balance)) / Number(balance) * 100)
        };
        exportToExcel(data, stats, lang);
        showToast('success', t('export.success'), t('export.excelDownloaded'));
    };

    const riskLabels = {
        worst: { label: 'En Kotu', class: 'worst' },
        bad: { label: 'Kotu', class: 'bad' },
        neutral: { label: 'Notr', class: 'neutral' },
        good: { label: 'Iyi', class: 'good' },
        best: { label: 'En Iyi', class: 'best' }
    };

    const currentParams = {
        balance: Number(balance),
        percentage: Number(percentage),
        targetDay: Number(targetDay),
        investmentRatio: Number(investmentRatio)
    };

    return (
        <div className="app-container">
            <Navbar onHistoryClick={() => setShowHistory(true)} />

            {/* Toast Container */}
            <div className="toast-container">
                <Toast toast={toast} onClose={() => setToast(null)} />
            </div>

            {/* Notification Popup */}
            {notification && (
                <div className="notification-popup">
                    <div className="notification-header">
                        <div className="notification-icon"><LuBell /></div>
                        <span className="notification-title">{notification.title}</span>
                    </div>
                    <p className="notification-message">{notification.message}</p>
                    {notification.targetAmount && (
                        <p style={{ color: 'var(--accent)', fontWeight: 600 }}>
                            {t('reminder.targetAmount')}: {currencyFormatterUsdt.format(notification.targetAmount)}
                        </p>
                    )}
                    <div className="notification-actions">
                        <button className="btn btn-secondary" onClick={dismissNotification}>
                            {t('reminder.dismiss')}
                        </button>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                shareUrl={generateShareUrl()}
                t={t}
            />

            {/* Reminder Modal */}
            <ReminderModal
                isOpen={showReminderModal}
                onClose={() => setShowReminderModal(false)}
                onAdd={handleAddReminder}
                currentAmount={getFinalBalance()}
                t={t}
            />

            {/* History Panel */}
            <div className={`history-overlay ${showHistory ? 'open' : ''}`} onClick={() => setShowHistory(false)} />
            <div className={`history-panel ${showHistory ? 'open' : ''}`}>
                <div className="history-panel-header">
                    <h3 className="history-panel-title">{t('history.title')}</h3>
                    <button className="history-close" onClick={() => setShowHistory(false)}>&times;</button>
                </div>
                <div className="history-list">
                    {history.length === 0 ? (
                        <EmptyState
                            icon="history"
                            title={t('history.emptyTitle')}
                            description={t('history.emptyDescription')}
                        />
                    ) : (
                        history.map(item => (
                            <div key={item.id} className="history-item" onClick={() => loadFromHistory(item)}>
                                <div className="history-item-date">
                                    {new Date(item.date).toLocaleDateString('tr-TR')}
                                </div>
                                <div className="history-item-summary">
                                    <span className="history-item-balance">
                                        {currencyFormatterUsdt.format(item.finalBalance)}
                                    </span>
                                    <span className="history-item-profit">
                                        +{currencyFormatterUsdt.format(item.totalProfit)}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Favorites Panel */}
            <div className={`history-overlay ${showFavorites ? 'open' : ''}`} onClick={() => setShowFavorites(false)} />
            <div className={`favorites-panel ${showFavorites ? 'open' : ''}`}>
                <div className="history-panel-header">
                    <h3 className="history-panel-title">{t('favorites.title')}</h3>
                    <button className="history-close" onClick={() => setShowFavorites(false)}>&times;</button>
                </div>
                <div className="history-list">
                    {favorites.length === 0 ? (
                        <EmptyState
                            icon="star"
                            title={t('favorites.emptyTitle')}
                            description={t('favorites.emptyDescription')}
                        />
                    ) : (
                        favorites.map(item => (
                            <div key={item.id} className="favorite-item" onClick={() => loadFromHistory(item)}>
                                <div className="favorite-item-name">
                                    <span className="favorite-star"><LuStar /></span>
                                    {item.name}
                                </div>
                                <div className="favorite-item-params">
                                    <span className="favorite-param">${item.balance}</span>
                                    <span className="favorite-param">{item.percentage}%</span>
                                    <span className="favorite-param">{item.targetDay} gun</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <main className="main-content">
                <header className="page-header">
                    <h1 className="page-title">{t('app.title')}</h1>
                    <p className="page-subtitle">{t('app.subtitle')}</p>
                </header>

                {/* Exchange Rate Widget */}
                <div className="exchange-rate-widget">
                    <div className="exchange-rate-header">
                        <span className="exchange-label">{lang === 'tr' ? 'Canli Doviz Kuru' : 'Live Exchange Rate'}</span>
                        <button
                            className="refresh-rate-btn"
                            onClick={refreshRates}
                            disabled={ratesLoading}
                            title={lang === 'tr' ? 'Yenile' : 'Refresh'}
                        >
                            <LuRefreshCw className={ratesLoading ? 'spinning' : ''} />
                        </button>
                    </div>
                    <div className="exchange-rates">
                        <div className="rate-item">
                            <span className="rate-pair">USD/TRY</span>
                            <span className="rate-value">
                                {ratesLoading ? '...' : rates.TRY.toFixed(4)}
                                <LuTrendingUp className="trend-icon up" />
                            </span>
                        </div>
                        <div className="rate-item">
                            <span className="rate-pair">EUR/USD</span>
                            <span className="rate-value">
                                {ratesLoading ? '...' : (1 / rates.EUR).toFixed(4)}
                            </span>
                        </div>
                        <div className="rate-item">
                            <span className="rate-pair">GBP/USD</span>
                            <span className="rate-value">
                                {ratesLoading ? '...' : (1 / rates.GBP).toFixed(4)}
                            </span>
                        </div>
                    </div>
                    {lastUpdated && (
                        <div className="rate-updated">
                            {lang === 'tr' ? 'Son guncelleme: ' : 'Last updated: '}
                            {lastUpdated.toLocaleTimeString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                        </div>
                    )}
                    {ratesError && (
                        <div className="rate-error">{lang === 'tr' ? 'Kur yuklenemedi (varsayilan degerler)' : 'Failed to load rates (using defaults)'}</div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'calculator' ? 'active' : ''}`}
                        onClick={() => setActiveTab('calculator')}
                    >
                        Hesaplama
                    </button>
                    <button
                        className={`tab ${activeTab === 'target' ? 'active' : ''}`}
                        onClick={() => setActiveTab('target')}
                    >
                        {t('targetCalc.title')}
                    </button>
                    <button
                        className={`tab ${activeTab === 'comparison' ? 'active' : ''}`}
                        onClick={() => setActiveTab('comparison')}
                    >
                        {t('comparison.title')}
                    </button>
                    <button
                        className={`tab ${activeTab === 'reminders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reminders')}
                    >
                        {t('reminder.title')}
                    </button>
                </div>

                {/* Main Calculator Tab */}
                {activeTab === 'calculator' && (
                    <>
                        <section className="form-section card" style={{ position: 'relative' }}>
                            {isLoading && (
                                <div className="loading-overlay">
                                    <div className="spinner"></div>
                                </div>
                            )}
                            <h2 className="card-title">{t('form.title')}</h2>

                            {/* Calculation Type & Period */}
                            <div className="form-grid" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label className="form-label">{t('form.calculationType')}</label>
                                    <select
                                        className="form-select"
                                        value={calcType}
                                        onChange={(e) => setCalcType(e.target.value)}
                                    >
                                        <option value={CALC_TYPES.COMPOUND}>{t('form.compound')}</option>
                                        <option value={CALC_TYPES.SIMPLE}>{t('form.simple')}</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('form.period')}</label>
                                    <select
                                        className="form-select"
                                        value={period}
                                        onChange={(e) => setPeriod(e.target.value)}
                                    >
                                        <option value={PERIODS.DAILY}>{t('form.daily')}</option>
                                        <option value={PERIODS.WEEKLY}>{t('form.weekly')}</option>
                                        <option value={PERIODS.MONTHLY}>{t('form.monthly')}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">{t('form.investmentRatio')}</label>
                                    <input
                                        type="number"
                                        className={`form-input ${touched.investmentRatio && errors.investmentRatio ? 'error' : ''}`}
                                        placeholder={t('form.placeholder.investmentRatio')}
                                        value={investmentRatio}
                                        onChange={(e) => setInvestmentRatio(e.target.value)}
                                        onBlur={() => handleBlur('investmentRatio', investmentRatio)}
                                    />
                                    {touched.investmentRatio && errors.investmentRatio && (
                                        <span className="form-error">⚠ {errors.investmentRatio}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('form.balance')}</label>
                                    <input
                                        type="number"
                                        className={`form-input ${touched.balance && errors.balance ? 'error' : ''}`}
                                        placeholder={t('form.placeholder.balance')}
                                        value={balance}
                                        onChange={(e) => setBalance(e.target.value)}
                                        onBlur={() => handleBlur('balance', balance)}
                                    />
                                    {touched.balance && errors.balance && (
                                        <span className="form-error">⚠ {errors.balance}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('form.targetDay')}</label>
                                    <input
                                        type="number"
                                        className={`form-input ${touched.targetDay && errors.targetDay ? 'error' : ''}`}
                                        placeholder={t('form.placeholder.targetDay')}
                                        value={targetDay}
                                        onChange={(e) => setTargetDay(e.target.value)}
                                        onBlur={() => handleBlur('targetDay', targetDay)}
                                    />
                                    {touched.targetDay && errors.targetDay && (
                                        <span className="form-error">⚠ {errors.targetDay}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('form.dailyPercent')}</label>
                                    <input
                                        type="number"
                                        className={`form-input ${touched.percentage && errors.percentage ? 'error' : ''}`}
                                        placeholder={t('form.placeholder.dailyPercent')}
                                        value={percentage}
                                        onChange={(e) => setPercentage(e.target.value)}
                                        onBlur={() => handleBlur('percentage', percentage)}
                                    />
                                    {touched.percentage && errors.percentage && (
                                        <span className="form-error">⚠ {errors.percentage}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('form.inflation')}</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder={t('form.placeholder.inflation')}
                                        value={inflation}
                                        onChange={(e) => setInflation(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button className="btn btn-primary" onClick={handleCalculate} disabled={isLoading}>
                                    {isLoading ? t('loading.calculating') : t('form.calculate')}
                                </button>
                                <button className="btn btn-secondary" onClick={handleReset}>
                                    {t('form.reset')}
                                </button>
                            </div>
                        </section>

                        {data.length > 0 && (
                            <>
                                {/* Action Buttons */}
                                <div className="action-buttons">
                                    <button
                                        className={`btn btn-icon btn-favorite ${isFavorite(currentParams) ? 'active' : ''}`}
                                        onClick={handleToggleFavorite}
                                        title={t('favorites.add')}
                                    >
                                        <LuStar />
                                    </button>
                                    <button
                                        className="btn btn-icon btn-share"
                                        onClick={() => setShowShareModal(true)}
                                        title={t('share.title')}
                                    >
                                        <LuShare2 />
                                    </button>
                                    <button
                                        className="btn btn-icon btn-compare"
                                        onClick={handleAddComparison}
                                        title={t('comparison.add')}
                                    >
                                        <LuLayoutGrid />
                                    </button>
                                    <button
                                        className="btn btn-icon btn-reminder"
                                        onClick={() => setShowReminderModal(true)}
                                        title={t('reminder.add')}
                                    >
                                        <LuBell />
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => setShowFavorites(true)}
                                        style={{ marginLeft: 'auto' }}
                                    >
                                        {t('nav.favorites')}
                                    </button>
                                </div>

                                {/* Stats Grid with Animated Numbers */}
                                <section className="stats-grid">
                                    <div className="stat-card accent-cyan">
                                        <div className="stat-icon">$</div>
                                        <p className="stat-label">{t('stats.finalBalance')}</p>
                                        <p className="stat-value">
                                            <AnimatedValue value={getFinalBalance()} formatter={currencyFormatterUsdt.format} />
                                        </p>
                                    </div>
                                    <div className="stat-card accent-green">
                                        <div className="stat-icon">+</div>
                                        <p className="stat-label">{t('stats.totalProfit')}</p>
                                        <p className="stat-value">
                                            <AnimatedValue value={getTotalProfit()} formatter={currencyFormatterUsdt.format} />
                                        </p>
                                    </div>
                                    <div className="stat-card accent-purple">
                                        <div className="stat-icon">~</div>
                                        <p className="stat-label">{t('stats.dailyAverage')}</p>
                                        <p className="stat-value">
                                            <AnimatedValue value={getAverageDaily()} formatter={currencyFormatterUsdt.format} />
                                        </p>
                                    </div>
                                    {inflation && (
                                        <div className="stat-card" style={{ borderColor: 'rgba(255, 107, 53, 0.3)' }}>
                                            <div className="stat-icon" style={{ background: 'rgba(255, 107, 53, 0.1)', color: '#ff6b35' }}>%</div>
                                            <p className="stat-label">{t('stats.realProfit')}</p>
                                            <p className="stat-value" style={{ color: '#ff6b35' }}>
                                                <AnimatedValue value={getRealProfit()} formatter={currencyFormatterUsdt.format} />
                                            </p>
                                        </div>
                                    )}
                                </section>

                                {/* Risk Analysis */}
                                {riskScenarios.length > 0 && (
                                    <section className="card mb-4">
                                        <h2 className="card-title">{t('risk.title')}</h2>
                                        <div className="risk-grid">
                                            {riskScenarios.map((scenario) => (
                                                <div key={scenario.name} className={`risk-card ${riskLabels[scenario.name].class}`}>
                                                    <div className="risk-label">{riskLabels[scenario.name].label}</div>
                                                    <div className="risk-value">{currencyFormatterUsdt.format(scenario.finalBalance)}</div>
                                                    <div className={`risk-change ${scenario.profit >= 0 ? 'positive' : 'negative'}`}>
                                                        {scenario.profit >= 0 ? '+' : ''}{scenario.profitPercent.toFixed(1)}%
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Chart */}
                                <section className="chart-section">
                                    <div className="card">
                                        <h2 className="card-title">{t('chart.title')}</h2>
                                        <div className="chart-container">
                                            <Line data={chartData} options={chartOptions} />
                                        </div>
                                    </div>
                                </section>

                                {/* Results Table */}
                                <section className="results-section">
                                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                                        <div className="results-header">
                                            <span>{t('results.day')}</span>
                                            <span>{t('results.totalBalance')}</span>
                                            <span>{t('results.tryEquivalent')}</span>
                                            <span>{t('results.dailyProfit')}</span>
                                        </div>
                                        <div className="results-list">
                                            {data.map((item, index) => (
                                                <div
                                                    className="result-row"
                                                    key={item.day}
                                                    style={{ animationDelay: `${Math.min(index * 0.05, 0.5)}s` }}
                                                >
                                                    <span className="result-day">{item.day}. {t('results.day')}</span>
                                                    <span className="result-balance">{currencyFormatterUsdt.format(item.usdt)}</span>
                                                    <span className="result-try">{currencyFormatter.format(item.try)}</span>
                                                    <span className="result-profit">+{currencyFormatterUsdt.format(item.profit)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Export Buttons */}
                                    <div className="export-buttons">
                                        <button className="btn btn-export btn-pdf" onClick={handleExportPDF}>
                                            {t('export.pdf')}
                                        </button>
                                        <button className="btn btn-export btn-excel" onClick={handleExportExcel}>
                                            {t('export.excel')}
                                        </button>
                                    </div>
                                </section>
                            </>
                        )}

                        {data.length === 0 && !isLoading && (
                            <EmptyState
                                icon="chart"
                                title={t('empty.title')}
                                description={t('empty.description')}
                            />
                        )}
                    </>
                )}

                {/* Target Calculator Tab */}
                {activeTab === 'target' && (
                    <section className="card">
                        <h2 className="card-title">{t('targetCalc.title')}</h2>
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">{t('form.balance')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder={t('form.placeholder.balance')}
                                    value={balance}
                                    onChange={(e) => setBalance(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('form.investmentRatio')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder={t('form.placeholder.investmentRatio')}
                                    value={investmentRatio}
                                    onChange={(e) => setInvestmentRatio(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('form.dailyPercent')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder={t('form.placeholder.dailyPercent')}
                                    value={percentage}
                                    onChange={(e) => setPercentage(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('targetCalc.targetAmount')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="orn: 10000"
                                    value={targetAmount}
                                    onChange={(e) => setTargetAmount(e.target.value)}
                                />
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={handleTargetCalculate}>
                            {t('targetCalc.calculate')}
                        </button>

                        {daysToTarget !== null && (
                            <div className="target-result">
                                <p className="target-result-label">{t('targetCalc.daysNeeded')}</p>
                                <p className="target-result-value">
                                    {daysToTarget === -1 ? 'Hesaplanamadi' : `${daysToTarget} Gun`}
                                </p>
                            </div>
                        )}
                    </section>
                )}

                {/* Comparison Tab */}
                {activeTab === 'comparison' && (
                    <section className="card">
                        <h2 className="card-title">{t('comparison.title')}</h2>

                        {comparisons.length === 0 ? (
                            <EmptyState
                                icon="chart"
                                title={t('comparison.title')}
                                description={t('comparison.empty')}
                            />
                        ) : (
                            <div className="comparison-container">
                                {comparisons.map((comp) => {
                                    const isBest = comparisons.every(c => comp.totalProfit >= c.totalProfit);
                                    return (
                                        <div key={comp.id} className={`comparison-card ${isBest ? 'active' : ''}`}>
                                            {isBest && <span className="comparison-badge">{t('comparison.best')}</span>}
                                            <div className="comparison-header">
                                                <span className="comparison-title">{comp.name}</span>
                                                <button
                                                    className="comparison-remove"
                                                    onClick={() => handleRemoveComparison(comp.id)}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                            <div className="comparison-stats">
                                                <div className="comparison-stat">
                                                    <div className="comparison-stat-label">{t('form.balance')}</div>
                                                    <div className="comparison-stat-value">${comp.params.balance}</div>
                                                </div>
                                                <div className="comparison-stat">
                                                    <div className="comparison-stat-label">{t('form.dailyPercent')}</div>
                                                    <div className="comparison-stat-value">{comp.params.percentage}%</div>
                                                </div>
                                                <div className="comparison-stat">
                                                    <div className="comparison-stat-label">{t('stats.finalBalance')}</div>
                                                    <div className="comparison-stat-value">{currencyFormatterUsdt.format(comp.finalBalance)}</div>
                                                </div>
                                                <div className="comparison-stat">
                                                    <div className="comparison-stat-label">{t('stats.totalProfit')}</div>
                                                    <div className="comparison-stat-value profit">{currencyFormatterUsdt.format(comp.totalProfit)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {comparisons.length < 3 && (
                                    <div className="add-comparison" onClick={() => setActiveTab('calculator')}>
                                        <div className="add-comparison-icon">+</div>
                                        <span className="add-comparison-text">{t('comparison.add')}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {/* Reminders Tab */}
                {activeTab === 'reminders' && (
                    <section className="card">
                        <h2 className="card-title">{t('reminder.title')}</h2>

                        <button
                            className="btn btn-primary mb-3"
                            onClick={() => setShowReminderModal(true)}
                        >
                            + {t('reminder.add')}
                        </button>

                        {reminders.length === 0 ? (
                            <EmptyState
                                icon="history"
                                title={t('reminder.emptyTitle')}
                                description={t('reminder.emptyDescription')}
                            />
                        ) : (
                            <div className="reminder-list">
                                {reminders.map(reminder => (
                                    <div key={reminder.id} className="reminder-item">
                                        <div className="reminder-info">
                                            <div className="reminder-title">{reminder.title}</div>
                                            <div className="reminder-date">
                                                {new Date(reminder.date).toLocaleString('tr-TR')}
                                            </div>
                                            {reminder.targetAmount && (
                                                <div className="reminder-target">
                                                    {t('reminder.targetAmount')}: {currencyFormatterUsdt.format(reminder.targetAmount)}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="reminder-delete"
                                            onClick={() => {
                                                removeReminder(reminder.id);
                                                showToast('info', t('toast.info'), t('reminder.deleted'));
                                            }}
                                        >
                                            <LuTrash2 />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
}

export default Home;
